import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui-kit/Dialog";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";
import { Select } from "@/components/ui-kit/Select";

// Optional courtesy titles. "" = none selected.
const SALUTATIONS = ["Mr.", "Ms.", "Mrs.", "Dr.", "Prof.", "Mx."];

const EMPTY = { salutation: "", first_name: "", last_name: "", email: "", phone: "", notes: "" };

// Split a legacy single-name client into first / last for editing.
function splitName(full = "") {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "", last: "" };
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

export function ClientFormDialog({ open, onOpenChange, client, prefill, onSaved }) {
  const isEdit = Boolean(client);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (client) {
        const legacy = splitName(client.name || "");
        setForm({
          salutation: client.salutation || "",
          first_name: client.first_name || legacy.first,
          last_name: client.last_name || legacy.last,
          email: client.email || "",
          phone: client.phone || "",
          notes: client.notes || "",
        });
      } else if (prefill) {
        const pn = splitName(prefill.name || "");
        setForm({
          salutation: "",
          first_name: prefill.first_name || pn.first,
          last_name: prefill.last_name || pn.last,
          email: prefill.email || "",
          phone: prefill.phone || "",
          notes: prefill.notes || "",
        });
      } else {
        setForm(EMPTY);
      }
      setError("");
    }
  }, [open, client, prefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError("First and last name are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        salutation: form.salutation,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email,
        phone: form.phone,
        notes: form.notes,
      };
      const saved = isEdit
        ? (await api.patch(`/clients/${client.id}`, payload)).data
        : (await api.post("/clients", payload)).data;
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="client-form-dialog">
        <DialogTitle>{isEdit ? "Edit client" : "New client"}</DialogTitle>
        <DialogDescription>
          Clients can hold many matters across separate legal issues.
        </DialogDescription>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div className="grid grid-cols-[110px_1fr] gap-3">
            <div>
              <Label htmlFor="client-salutation">Salutation</Label>
              <Select
                id="client-salutation"
                data-testid="client-salutation-select"
                value={form.salutation}
                onChange={set("salutation")}
              >
                <option value="">(none)</option>
                {SALUTATIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="client-first-name">First name</Label>
              <Input
                id="client-first-name"
                data-testid="client-first-name-input"
                value={form.first_name}
                onChange={set("first_name")}
                placeholder="e.g. Nancy"
                autoFocus
              />
            </div>
          </div>
          <div>
            <Label htmlFor="client-last-name">Last name</Label>
            <Input
              id="client-last-name"
              data-testid="client-last-name-input"
              value={form.last_name}
              onChange={set("last_name")}
              placeholder="e.g. Jones"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="client-email">Email</Label>
              <Input
                id="client-email"
                type="email"
                data-testid="client-email-input"
                value={form.email}
                onChange={set("email")}
                placeholder="optional"
              />
            </div>
            <div>
              <Label htmlFor="client-phone">Phone</Label>
              <Input
                id="client-phone"
                data-testid="client-phone-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder="optional"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="client-notes">Notes</Label>
            <Input
              id="client-notes"
              data-testid="client-notes-input"
              value={form.notes}
              onChange={set("notes")}
              placeholder="optional"
            />
          </div>

          {error && (
            <p
              data-testid="client-form-error"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="client-form-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? "Save changes" : "Create client"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
